import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../services/api'
import AudioRecorder from './AudioRecorder'
import { useInterviewChannel } from '../../hooks/useInterviewChannel'

// The member respond interface. An initiative can have SEVERAL active
// prompts (2026-07): this panel steps the member through them one at a
// time in queue order — linear, with back-navigation to already-answered
// steps — and ends on a summary listing every prompt + answer with Edit.
// Used identically by /initiatives/:slug/respond and inline on Home.

type Modality = 'text' | 'voice'

interface CurrentResponse {
  id: number
  modality: string
  status: string
  raw_input: string | null
  structured_response: string | null
  received_at: string
}

export interface ActivationRow {
  id: number
  status: string
  activated_at: string
  prompt: {
    id: number
    question_text: string
    follow_up_instructions: string | null
  }
  current_response: CurrentResponse | null
}

export interface RespondState {
  initiative: {
    id: number
    slug: string
    name: string
    description: string | null
    allow_edits: boolean
  } | null
  activations: ActivationRow[]
}

interface RespondPanelProps {
  slug: string | undefined
  // When the parent already fetched the respond state (Home does, to decide
  // which initiatives need the inline form), pass it in — the panel renders
  // instantly instead of re-fetching and flashing its own loading state.
  initialState?: RespondState
  // Deep-link target: open the step for this activation (from "Open in
  // Respond" on /me/responses) instead of the first unanswered prompt. Only
  // honored on the fetch path (standalone Respond page), not the inline Home
  // panels, which never pass it.
  initialActivationId?: number
}

const firstUnansweredIndex = (activations: ActivationRow[]) =>
  activations.findIndex((a) => !a.current_response)

// The upload filename's extension survives all the way to the Whisper API
// call (ActiveStorage keeps it on the tempfile WhisperClient reads), and
// Whisper validates by extension: flac, m4a, mp3, mp4, mpeg, mpga, oga,
// ogg, wav, webm. The old `type.includes('webm') ? 'webm' : 'audio'`
// mapping sent Safari/iOS recordings (audio/mp4) up as `response.audio`,
// which Whisper 400s — i.e. a guaranteed "transcription error".
const audioExtension = (type: string): string => {
  const t = type.toLowerCase()
  if (t.includes('webm')) return 'webm'
  if (t.includes('mp4') || t.includes('m4a') || t.includes('aac')) return 'm4a'
  if (t.includes('ogg') || t.includes('opus')) return 'ogg'
  if (t.includes('mpeg') || t.includes('mp3')) return 'mp3'
  if (t.includes('wav')) return 'wav'
  return 'webm'
}

export default function RespondPanel({ slug, initialState, initialActivationId }: RespondPanelProps) {
  const [state, setState] = useState<RespondState | null>(initialState ?? null)
  const [loading, setLoading] = useState(!initialState)
  const [error, setError] = useState<string | null>(null)
  const [modality, setModality] = useState<Modality>('text')
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [flash, setFlash] = useState<string | null>(null)

  // Step machine: which activation is on screen, or the end-state summary.
  const [stepIdx, setStepIdx] = useState<number>(() => {
    if (!initialState) return 0
    const i = firstUnansweredIndex(initialState.activations)
    return i === -1 ? 0 : i
  })
  const [showSummary, setShowSummary] = useState<boolean>(() => {
    if (!initialState) return false
    return (
      initialState.activations.length > 0 &&
      firstUnansweredIndex(initialState.activations) === -1
    )
  })
  const [draft, setDraft] = useState<string>(() => {
    if (!initialState) return ''
    const i = firstUnansweredIndex(initialState.activations)
    const row = initialState.activations[i === -1 ? 0 : i]
    return row?.current_response?.raw_input ?? ''
  })

  const activations = state?.activations ?? []
  const current: ActivationRow | undefined = activations[stepIdx]
  const answeredCount = activations.filter((a) => a.current_response).length
  const hasExisting = !!current?.current_response

  const canEdit = useMemo(() => {
    if (!state?.initiative || !current) return false
    if (!hasExisting) return true
    return state.initiative.allow_edits
  }, [state, current, hasExisting])

  // Jump to a step, resetting per-step form state. Linear-with-back rule:
  // answered steps and the first unanswered step are reachable; later
  // unanswered steps are locked until the flow gets there.
  const goToStep = (idx: number) => {
    const row = activations[idx]
    if (!row) return
    setStepIdx(idx)
    setShowSummary(false)
    setDraft(row.current_response?.raw_input ?? '')
    setAudioBlob(null)
    setModality('text')
    setFlash(null)
    setError(null)
  }

  const stepReachable = (idx: number) => {
    const row = activations[idx]
    if (!row) return false
    if (row.current_response) return true
    const first = firstUnansweredIndex(activations)
    return idx === first
  }

  // ---- Live status updates over ActionCable ------------------------------
  // Subscribe to the CURRENT step's activation; only react to events for the
  // user's own response on that step (ref keeps the callback stable).
  const currentResponseIdRef = useRef<number | null>(null)
  currentResponseIdRef.current = current?.current_response?.id ?? null
  const stepIdxRef = useRef(stepIdx)
  stepIdxRef.current = stepIdx

  const handleChannelMessage = useCallback((data: any) => {
    if (!data || data.response_id !== currentResponseIdRef.current) return

    setState((prev) => {
      if (!prev) return prev
      const idx = stepIdxRef.current
      const row = prev.activations[idx]
      if (!row?.current_response || row.current_response.id !== data.response_id) return prev

      const patchResponse = (patch: Partial<CurrentResponse>) => ({
        ...prev,
        activations: prev.activations.map((a, i) =>
          i === idx ? { ...a, current_response: { ...a.current_response!, ...patch } } : a,
        ),
      })

      switch (data.type) {
        case 'response_transcribed':
          return patchResponse({
            status: 'transcribed',
            raw_input: data.raw_input_preview ?? row.current_response.raw_input,
          })
        case 'response_transcription_failed':
          return patchResponse({ status: 'transcription_failed' })
        case 'response_structured':
          return patchResponse({
            status: 'analyzed',
            structured_response: data.structured_response ?? null,
          })
        default:
          return prev
      }
    })

    if (data.type === 'response_transcribed') {
      setFlash('Transcribed. Analyzing…')
    } else if (data.type === 'response_structured') {
      setFlash('Analysis ready below.')
    } else if (data.type === 'response_transcription_failed') {
      setFlash(null)
      setError('Transcription failed. Please try recording again.')
    }
  }, [])

  useInterviewChannel(!showSummary ? (current?.id ?? null) : null, handleChannelMessage)

  useEffect(() => {
    // Parent-provided state — nothing to fetch.
    if (initialState) return undefined

    let cancelled = false

    async function load() {
      if (!slug) return
      setLoading(true)
      setError(null)
      try {
        const data: RespondState = await api.get(`/api/v1/initiatives/${slug}/respond`)
        if (cancelled) return
        setState(data)
        // Deep-link target (from /me/responses) wins over the default
        // first-unanswered landing, as long as that activation is still
        // active/returned. Otherwise fall back to the normal behavior.
        const targetIdx =
          initialActivationId != null
            ? data.activations.findIndex((a) => a.id === initialActivationId)
            : -1
        if (targetIdx !== -1) {
          setShowSummary(false)
          setStepIdx(targetIdx)
          setDraft(data.activations[targetIdx]?.current_response?.raw_input ?? '')
        } else {
          const i = firstUnansweredIndex(data.activations)
          if (data.activations.length > 0 && i === -1) {
            setShowSummary(true)
          } else {
            const idx = i === -1 ? 0 : i
            setStepIdx(idx)
            setDraft(data.activations[idx]?.current_response?.raw_input ?? '')
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  const canSubmit = useMemo(() => {
    if (submitting || !canEdit) return false
    if (modality === 'text') return draft.trim().length > 0
    return audioBlob !== null
  }, [submitting, canEdit, modality, draft, audioBlob])

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!slug || !current || !canSubmit) return

    setSubmitting(true)
    setFlash(null)
    setError(null)
    try {
      let created: CurrentResponse
      if (modality === 'text') {
        created = await api.post(`/api/v1/initiatives/${slug}/responses`, {
          prompt_activation_id: current.id,
          raw_input: draft.trim(),
        })
      } else {
        const formData = new FormData()
        formData.append('modality', 'audio')
        formData.append('prompt_activation_id', String(current.id))
        // Filename matters: Whisper validates by extension (see audioExtension).
        formData.append('audio', audioBlob!, `response.${audioExtension(audioBlob!.type)}`)
        created = await api.postForm(`/api/v1/initiatives/${slug}/responses`, formData)
      }

      const updated = activations.map((a, i) =>
        i === stepIdx ? { ...a, current_response: created } : a,
      )
      setState((prev) => (prev ? { ...prev, activations: updated } : prev))

      // Advance: next unanswered prompt, or the end-state summary.
      const next = firstUnansweredIndex(updated)
      if (next === -1) {
        setShowSummary(true)
        setAudioBlob(null)
      } else {
        setStepIdx(next)
        setDraft(updated[next].current_response?.raw_input ?? '')
        setAudioBlob(null)
        setModality('text')
        setFlash('Answer saved — next prompt.')
      }
    } catch (e: any) {
      setError(e?.message || 'Submission failed')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div className="text-sm text-slate-500">Loading…</div>
  }

  if (error && !state) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        {error}
      </div>
    )
  }

  if (!state || !state.initiative) {
    return <div className="text-sm text-slate-500">Initiative not found.</div>
  }

  const { initiative } = state

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        {hasExisting && !showSummary && (
          <div className="text-xs uppercase tracking-wide text-slate-500">Edit your response</div>
        )}
        <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
          {initiative.name}
        </h2>
      </div>

      {activations.length === 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-6">
          <p className="text-sm text-slate-700">
            There is no active prompt for this initiative right now. Check back later.
          </p>
          <Link to="/" className="mt-4 inline-block text-sm text-accent-600 hover:underline">
            ← Back to Home
          </Link>
        </div>
      )}

      {/* Step indicator */}
      {activations.length > 1 && (
        <nav aria-label="Prompt progress" className="flex flex-wrap items-center gap-2">
          {activations.map((a, i) => {
            const answered = !!a.current_response
            const isCurrent = !showSummary && i === stepIdx
            const reachable = stepReachable(i)
            const base =
              'inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition'
            const cls = isCurrent
              ? 'bg-accent-600 text-white'
              : answered
                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                : 'bg-slate-100 text-slate-400'
            return (
              <button
                key={a.id}
                type="button"
                disabled={!reachable || submitting}
                onClick={() => goToStep(i)}
                title={a.prompt.question_text}
                aria-label={`Prompt ${i + 1}${answered ? ' (answered)' : ''}`}
                aria-current={isCurrent ? 'step' : undefined}
                className={`${base} ${cls} ${!reachable ? 'cursor-not-allowed' : ''}`}
              >
                {answered && !isCurrent ? '✓' : i + 1}
              </button>
            )
          })}
          <span className="ml-2 text-xs text-slate-500">
            {answeredCount} of {activations.length} answered
          </span>
        </nav>
      )}

      {/* ---- End state: everything answered --------------------------------- */}
      {showSummary && activations.length > 0 && (
        <div className="space-y-4">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-sm font-medium text-emerald-800">
              You're all caught up — every active prompt is answered. Thank you!
            </p>
            <p className="mt-1 text-xs text-emerald-700">
              Your answers are being analyzed and added to the Collective knowledge base. <br />A digest will be sent out after everyone has completed their responses.
            </p>
          </div>

          {!initiative.allow_edits && (
            <p className="text-xs text-slate-500">
              Editing answers is disabled for this initiative.
            </p>
          )}

          <ol className="space-y-3">
            {activations.map((a, i) => (
              <li key={a.id} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      Prompt {i + 1}
                    </div>
                    <p className="mt-1 text-sm font-medium text-slate-800">
                      {a.prompt.question_text}
                    </p>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600 line-clamp-4">
                      {a.current_response?.raw_input || '(voice answer — transcript pending)'}
                    </p>
                  </div>
                  {initiative.allow_edits && (
                    <button
                      type="button"
                      onClick={() => goToStep(i)}
                      className="shrink-0 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Edit
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* ---- Step view: one prompt at a time -------------------------------- */}
      {!showSummary && current && (
        <>
          <div className="rounded-md bg-slate-100 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">
              {activations.length > 1 ? `Prompt ${stepIdx + 1} of ${activations.length}` : 'Active prompt'}
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">
              {current.prompt.question_text}
            </p>
          </div>

          {hasExisting && !initiative.allow_edits && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              You've already submitted an answer for this prompt. Editing is disabled for this
              initiative.
            </div>
          )}

          {hasExisting && initiative.allow_edits && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              You've already submitted an answer. Edits will replace your previous one.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <fieldset className="flex items-center gap-4" disabled={!canEdit || submitting}>
              <legend className="sr-only">Response format</legend>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name={`modality-${initiative.slug}-${current.id}`}
                  value="text"
                  checked={modality === 'text'}
                  onChange={() => setModality('text')}
                  className="text-accent-600 focus:ring-accent-500"
                />
                Text
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name={`modality-${initiative.slug}-${current.id}`}
                  value="voice"
                  checked={modality === 'voice'}
                  onChange={() => setModality('voice')}
                  className="text-accent-600 focus:ring-accent-500"
                />
                Voice (max 5 min)
              </label>
            </fieldset>

            {modality === 'text' ? (
              <>
                <label
                  htmlFor={`response-${initiative.slug}-${current.id}`}
                  className="block text-sm font-medium text-slate-700"
                >
                  Your answer
                </label>
                <textarea
                  id={`response-${initiative.slug}-${current.id}`}
                  className="w-full min-h-[200px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 disabled:bg-slate-50 disabled:text-slate-500"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  disabled={!canEdit || submitting}
                  placeholder="Write a few sentences. Concrete details and examples are most useful."
                />
              </>
            ) : (
              <div>
                <p className="block text-sm font-medium text-slate-700 mb-2">Record your answer</p>
                <AudioRecorder onChange={setAudioBlob} disabled={!canEdit || submitting} />
                <p className="mt-2 text-xs text-slate-500">
                  We transcribe the recording on our servers and then delete the audio. Only the
                  text is retained.
                </p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-accent-500 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {submitting
                  ? modality === 'voice'
                    ? 'Uploading…'
                    : 'Submitting…'
                  : hasExisting
                    ? 'Save edit'
                    : 'Submit answer'}
              </button>

              {/* An answered step can bail back to the summary without saving. */}
              {hasExisting && answeredCount === activations.length && (
                <button
                  type="button"
                  onClick={() => setShowSummary(true)}
                  className="text-sm text-slate-500 hover:text-slate-700"
                >
                  Back to summary
                </button>
              )}

              {flash && <span className="text-sm text-emerald-700">{flash}</span>}
              {error && <span className="text-sm text-rose-700">{error}</span>}
            </div>
          </form>

          {current.current_response?.structured_response && (
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">Analysis</div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                {current.current_response.structured_response}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
